<script setup lang="ts">
/* eslint-disable vue/no-undef-components */
import { Rocket, Wrench, ArrowLeft } from 'lucide-vue-next';
import AppFooter from '@/components/AppFooter.vue';
import { i18n, t } from '@/i18n';
import {
  absoluteLocalizedUrl,
  buildPathHreflangLinks,
  localizedPath,
  ogLocaleFor,
  siteLocaleFromRouteParam,
} from '@/lib/site-locale';

const route = useRoute();
const slug = route.params.slug as string;
const siteLocale = computed(() => siteLocaleFromRouteParam(route.params.locale));
const helpPath = (path: string) => localizedPath(siteLocale.value, path);

type HelpSectionKey = 'getting_started' | 'troubleshooting';

const slugMeta: Record<
  string,
  {
    i18nKey: HelpSectionKey;
    icon: typeof Rocket;
    isTroubleshooting?: boolean;
  }
> = {
  'getting-started': { i18nKey: 'getting_started', icon: Rocket },
  troubleshooting: { i18nKey: 'troubleshooting', icon: Wrench, isTroubleshooting: true },
};

// Redirect old routes
if (slug === 'account-billing') {
  navigateTo(helpPath('/pricing'), { replace: true });
}
if (slug === 'features-tools') {
  navigateTo(helpPath('/ai-design-tools'), { replace: true });
}

const meta = slugMeta[slug];

const category = computed(() => {
  if (!meta) return null;
  const base = `landingPages.help.${meta.i18nKey}`;
  const contentRaw =
    meta.i18nKey === 'getting_started' ? i18n.getRaw(`${base}.content`) : undefined;

  return {
    icon: meta.icon,
    title: t(`${base}.title`),
    description: t(`${base}.description`),
    seoTitle: t(`${base}.seo.title`),
    seoDescription: t(`${base}.seo.description`),
    seoKeywords: t(`${base}.seo.keywords`),
    isTroubleshooting: meta.isTroubleshooting,
    content: Array.isArray(contentRaw)
      ? (contentRaw as Array<{ title: string; body: string }>)
      : undefined,
  };
});

const canonicalUrl = computed(() => absoluteLocalizedUrl(siteLocale.value, `/help/${slug}`));
const pageTitle = computed(
  () => category.value?.seoTitle || t('landingPages.help.common.seo.fallback_title')
);
const pageDescription = computed(
  () => category.value?.seoDescription || t('landingPages.help.common.seo.fallback_description')
);
const pageKeywords = computed(() => category.value?.seoKeywords || '');

const requestURL = useRequestURL();
const ogImageUrl = computed(() => `${requestURL.origin}/Veeso_og.jpg`);

useHead(() => ({
  title: pageTitle.value,
  htmlAttrs: { lang: siteLocale.value },
  meta: [
    { name: 'description', content: pageDescription.value },
    { name: 'keywords', content: pageKeywords.value },
    { property: 'og:title', content: pageTitle.value },
    { property: 'og:type', content: 'website' },
    { property: 'og:url', content: canonicalUrl.value },
    { property: 'og:locale', content: ogLocaleFor(siteLocale.value) },
    { property: 'og:description', content: pageDescription.value },
    { property: 'og:image', content: ogImageUrl.value },
    { property: 'og:image:width', content: '1200' },
    { property: 'og:image:height', content: '630' },
    { name: 'twitter:card', content: 'summary_large_image' },
    { name: 'twitter:site', content: '@veesoai' },
    { name: 'twitter:title', content: pageTitle.value },
    { name: 'twitter:description', content: pageDescription.value },
    { name: 'twitter:image', content: ogImageUrl.value },
  ],
  link: [
    { rel: 'canonical', href: canonicalUrl.value },
    ...buildPathHreflangLinks(`/help/${slug}`),
  ],
}));
</script>

<template>
  <div class="min-h-screen bg-white">
    <AppNav active-path="/help" />

    <div v-if="category" class="pt-32 pb-24 container mx-auto px-6 md:px-4 max-w-4xl">
      <NuxtLink
        :to="helpPath('/help')"
        class="inline-flex items-center text-black/60 hover:text-black mb-8 transition-colors group"
      >
        <ArrowLeft class="w-4 h-4 mr-2 group-hover:-translate-x-1 transition-transform" />
        {{ t('landingPages.help.common.back') }}
      </NuxtLink>

      <div class="flex items-center gap-4 mb-6">
        <div class="p-3 bg-black/5 rounded-2xl">
          <component :is="category.icon" class="w-8 h-8" />
        </div>
        <h1 class="font-boldonse text-4xl md:text-5xl uppercase tracking-tight">
          {{ category.title }}
        </h1>
      </div>

      <p class="text-xl text-black/60 mb-16 leading-relaxed">
        {{ category.description }}
      </p>

      <!-- Troubleshooting: Support Cards -->
      <div v-if="category.isTroubleshooting" class="grid grid-cols-1 md:grid-cols-2 gap-6">
        <a
          href="https://discord.gg/twbZEHkuWx"
          target="_blank"
          rel="noopener noreferrer"
          class="flex flex-col p-8 rounded-2xl bg-white border border-black/5 overflow-hidden transition-all duration-300 hover:bg-black/5 hover:scale-[1.02] group"
        >
          <div
            class="aspect-square w-full rounded-xl overflow-hidden mb-4 bg-black/5 flex items-center justify-center"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill="currentColor"
              class="w-16 h-16 text-[#5865F2]"
            >
              <path
                d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515a.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0a12.64 12.64 0 0 0-.617-1.25a.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057a19.9 19.9 0 0 0 5.993 3.03a.078.078 0 0 0 .084-.028a14.09 14.09 0 0 0 1.226-1.994a.076.076 0 0 0-.041-.106a13.107 13.107 0 0 1-1.872-.892a.077.077 0 0 1-.008-.128a10.2 10.2 0 0 0 .372-.292a.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127a12.299 12.299 0 0 1-1.873.892a.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028a19.839 19.839 0 0 0 6.002-3.03a.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419c0-1.333.956-2.419 2.157-2.419c1.21 0 2.176 1.096 2.157 2.42c0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419c0-1.333.955-2.419 2.157-2.419c1.21 0 2.176 1.096 2.157 2.42c0 1.333-.946 2.418-2.157 2.418z"
              />
            </svg>
          </div>
          <h2 class="text-xl md:text-2xl font-medium text-black/90 mb-2">
            {{ t('landingPages.help.troubleshooting.discord.title') }}
          </h2>
          <p class="text-base text-black/60 font-normal mb-4">
            {{ t('landingPages.help.troubleshooting.discord.description') }}
          </p>
          <span class="text-base font-medium text-black/80 mt-auto inline-flex items-center gap-2">
            {{ t('landingPages.help.troubleshooting.discord.cta') }}
            <span class="transition-transform group-hover:translate-x-1">→</span>
          </span>
        </a>

        <a
          href="mailto:help@veeso.ai"
          class="flex flex-col p-8 rounded-2xl bg-white border border-black/5 overflow-hidden transition-all duration-300 hover:bg-black/5 hover:scale-[1.02] group"
        >
          <div
            class="aspect-square w-full rounded-xl overflow-hidden mb-4 bg-black/5 flex items-center justify-center"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              class="w-16 h-16 text-black/60"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
              />
            </svg>
          </div>
          <h2 class="text-xl md:text-2xl font-medium text-black/90 mb-2">
            {{ t('landingPages.help.troubleshooting.email.title') }}
          </h2>
          <p class="text-base text-black/60 font-normal mb-4">
            {{ t('landingPages.help.troubleshooting.email.description') }}
          </p>
          <span class="text-base font-medium text-black/80 mt-auto inline-flex items-center gap-2">
            {{ t('landingPages.help.troubleshooting.email.cta') }}
            <span class="transition-transform group-hover:translate-x-1">→</span>
          </span>
        </a>
      </div>

      <!-- Regular Content -->
      <div v-else-if="category.content" class="space-y-12">
        <div
          v-for="(item, index) in category.content"
          :key="index"
          class="border-b border-black/5 pb-12 last:border-0"
        >
          <h2 class="text-2xl font-medium mb-4 text-black/90">{{ item.title }}</h2>
          <p class="text-lg text-black/70 leading-relaxed">{{ item.body }}</p>
        </div>
      </div>
    </div>

    <div v-else class="min-h-[60vh] flex flex-col items-center justify-center pt-24">
      <h1 class="text-3xl font-bold mb-4">
        {{ t('landingPages.help.common.not_found.title') }}
      </h1>
      <NuxtLink :to="helpPath('/help')" class="text-blue-600 hover:underline">
        {{ t('landingPages.help.common.not_found.link') }}
      </NuxtLink>
    </div>
    <AppFooter />
  </div>
</template>
